import { sub, toPixels } from "../shared/fixed";
import type { MatchRules, SuperDef } from "./ability";
import { hurtbox, MAX_JUGGLE, worldRect } from "./combat";
import { PRESS_BUFFER } from "./command";
import type { SimEvent } from "./events";
import { setState } from "./transition";
import { FIGHTER, type FighterState, type MatchState } from "./state";

export const FULL_METER = 3000;

/**
 * Суперприём (docs/design/abilities.md, Задачи/суперприём.md): по полной Ярости L2 + R2 — рывок с открывающим
 * ударом. Ярость сгорает сразу. Попал — заставка по кадрам мира: серия ударов и бросок через арену; таймер раунда
 * стоит. Всё — целые числа и кадры мира: онлайн у обоих одинаково, откат не ломает.
 */
export function trySuper(
  fighter: FighterState,
  rules: MatchRules,
  side: 0 | 1,
  frame: number,
  emit: (event: SimEvent) => void,
): boolean {
  const move = rules.supers[side];
  if (!move || fighter.meter < FULL_METER || fighter.superAge > PRESS_BUFFER) return false;
  fighter.meter = 0;
  fighter.superAge = 99;
  fighter.ability = -1;
  fighter.hasHit = 0;
  setState(fighter, FIGHTER.superAttack);
  emit({ kind: "superStart", frame, who: side, x: toPixels(fighter.x), y: -90, strength: 2, ability: move.id });
  return true;
}

/** Открывающий удар: рывок вперёд, окно попадания; промах — восстановление и обратно в стойку. */
export function updateOpener(fighter: FighterState, move: SuperDef): void {
  const frame = fighter.stateFrame;
  if (frame === 2) fighter.vx = sub(move.opener.lunge) * fighter.facing;
  if (frame >= move.opener.startup + move.opener.active + move.opener.recovery) setState(fighter, FIGHTER.idle);
}

/** Попал ли открывающий удар: тогда начинается заставка. Зовётся из шага мира после движения. */
export function checkOpener(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  if (state.cinematic >= 0) return;
  for (const side of [0, 1] as const) {
    const attacker = state.fighters[side];
    const move = rules.supers[side];
    if (!move || attacker.state !== FIGHTER.superAttack || attacker.hasHit) continue;
    const frame = attacker.stateFrame;
    if (frame < move.opener.startup || frame >= move.opener.startup + move.opener.active) continue;
    const victimSide: 0 | 1 = side === 0 ? 1 : 0;
    const victim = state.fighters[victimSide];
    const body = hurtbox(victim, rules, victimSide);
    if (!body) continue;
    const strike = worldRect(attacker, move.opener.hitbox);
    const hurt = worldRect(victim, body);
    if (!(strike.left < hurt.right && hurt.left < strike.right && strike.top < hurt.bottom && hurt.top < strike.bottom))
      continue;
    attacker.hasHit = 1;
    attacker.vx = 0;
    state.cinematic = side;
    state.cinematicFrame = 0;
    victim.ability = -1;
    victim.invisible = 0; // под суперприёмом невидимость слетает: сцену видно целиком
    victim.vx = 0;
    victim.vy = 0;
    victim.y = 0;
    // пара целиком в арене: жертва не за краем, при нужде обоих сдвигает внутрь
    const half = sub(rules.heroes[victimSide].width / 2);
    const low = sub(rules.arena.left) + half;
    const high = sub(rules.arena.right) - half;
    const wanted = attacker.x + sub(move.holdDistance) * attacker.facing;
    const shift = wanted > high ? high - wanted : wanted < low ? low - wanted : 0;
    attacker.x += shift;
    victim.x = wanted + shift;
    victim.facing = -attacker.facing;
    setState(victim, FIGHTER.superVictim);
    events.push({
      kind: "superHit",
      frame: state.frame,
      who: side,
      x: toPixels(victim.x),
      y: -90,
      strength: 2,
      ability: move.id,
    });
  }
}

/** Кадр заставки: удары серии по расписанию, в конце — бросок через арену. Мир вне заставки стоит. */
export function updateCinematic(state: MatchState, rules: MatchRules, events: SimEvent[]): void {
  const side = state.cinematic as 0 | 1;
  const move = rules.supers[side];
  const attacker = state.fighters[side];
  const victim = state.fighters[side === 0 ? 1 : 0];
  if (!move) {
    state.cinematic = -1;
    return;
  }
  state.cinematicFrame++;
  attacker.stateFrame++;
  victim.stateFrame++;
  const frame = state.cinematicFrame;
  const point = (strength: number, last: boolean): SimEvent => ({
    kind: "hit",
    frame: state.frame,
    who: side,
    x: toPixels(victim.x) - 10 * attacker.facing,
    y: last ? -110 : -100,
    strength,
    ability: move.id,
    fx: "heavy/spark",
  });
  for (const hit of move.hits) {
    if (hit.frame !== frame) continue;
    victim.health = Math.max(1, victim.health - hit.damage);
    events.push(point(1, false));
  }
  if (frame === move.finale.frame) {
    victim.health = Math.max(0, victim.health - move.finale.damage);
    setState(victim, victim.health === 0 ? FIGHTER.ko : FIGHTER.airHit);
    victim.vy = -sub(move.finale.launch);
    victim.vx = sub(move.finale.push) * attacker.facing;
    victim.juggle = MAX_JUGGLE;
    events.push(point(2, true));
    events.push({
      kind: "superFinale",
      frame: state.frame,
      who: side,
      x: toPixels(victim.x),
      y: -100,
      strength: 2,
      ability: move.id,
    });
  }
  if (frame >= move.length) {
    state.cinematic = -1;
    state.cinematicFrame = 0;
    setState(attacker, FIGHTER.idle);
  }
}
